/**
 * One-time backfill for the holding-period + realized-gain fields added in
 * Parts 3/4. Existing sales and trades were saved before those fields existed,
 * so this walks each one, looks up the source lot's Acq Date, and fills in
 * Acq Date / Holding Days / Tax Treatment / Realized Gain per item (plus the
 * sale-level "Has Unknown Holding" flag and trade-level "Realized Gain").
 *
 * Idempotent: items that already carry a Tax Treatment are skipped, so this
 * only writes rows that actually need classifying and is safe to run on every
 * load. Runs at sign-in from main.ts, after the rip-basis heal.
 */
import { DB } from "./db";
import { holdingPeriod } from "./taxation";

/** Best-effort Acq Date for a sold/traded line. Bulk uses the disposition
 *  date (pool is fungible → short-term); lot-linked items read the source
 *  lot; free-text items have none. */
function acqDateFor(item: any, dispositionDate: string): string {
  if (item.Type === "Bulk") return dispositionDate;
  const lotId = item["Lot ID"];
  if (!lotId) return "";
  const sealed = DB.table("sealed").find(r => r["Lot ID"] === lotId);
  const card   = DB.table("cards").find(r => r["Card Lot ID"] === lotId);
  return (sealed?.["Acq Date"] || card?.["Acq Date"] || "") as string;
}

export function backfillTaxFields(): number {
  let changed = 0;

  // ---- Sales ----
  for (const sale of DB.table("sales")) {
    if (String(sale["Status"] || "Completed") !== "Completed") continue;
    const items: any[] = Array.isArray(sale.Items) ? sale.Items : [];
    if (!items.length) continue;
    let touched = false;
    for (const it of items) {
      if (it["Tax Treatment"]) continue; // already classified
      const acq = acqDateFor(it, sale["Date"]);
      const hp = holdingPeriod(acq, sale["Date"]);
      it["Acq Date"]      = acq;
      it["Holding Days"]  = hp.days;
      it["Tax Treatment"] = hp.treatment;
      it["Realized Gain"] = +(((Number(it["Sale Price"]) || 0) - (Number(it["COGS"]) || 0))).toFixed(2);
      touched = true;
    }
    if (touched) {
      const unknown = items.some(it => it["Tax Treatment"] === "Unknown");
      DB.update("sales", "Sale ID", sale["Sale ID"], { "Items": items, "Has Unknown Holding": unknown });
      changed++;
    }
  }

  // ---- Trades ----
  for (const tr of DB.table("trades")) {
    if (String(tr["Status"] || "Accepted") !== "Accepted") continue;
    const out: any[] = Array.isArray(tr.Outgoing) ? tr.Outgoing : [];
    if (!out.length) continue;
    let touched = false;
    for (const it of out) {
      if (it["Tax Treatment"]) continue;
      const qty = Number(it.Qty) || 1;
      const acq = acqDateFor(it, tr["Date"]);
      const hp = holdingPeriod(acq, tr["Date"]);
      it["Acq Date"]      = acq;
      it["Holding Days"]  = hp.days;
      it["Tax Treatment"] = hp.treatment;
      it["Realized Gain"] = +(((Number(it.FMV) || 0) - (Number(it.Basis) || 0)) * qty).toFixed(2);
      touched = true;
    }
    if (touched) {
      const totalRealized = +out.reduce((s, it) => s + (Number(it["Realized Gain"]) || 0), 0).toFixed(2);
      DB.update("trades", "Trade ID", tr["Trade ID"], { "Outgoing": out, "Realized Gain": totalRealized });
      changed++;
    }
  }

  return changed;
}
